class WagmadeProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.productReady === 'true') return;
    this.dataset.productReady = 'true';

    this.product = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '{}');
    this.form = this.querySelector('.wagmade-product__form');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.optionInputs = Array.from(this.querySelectorAll('[data-option-input]'));
    this.addButton = this.querySelector('[data-add-button]');
    this.addButtonText = this.querySelector('[data-add-button-text]');
    this.currentPrice = this.querySelector('[data-current-price]');
    this.comparePrice = this.querySelector('[data-compare-price]');
    this.saleBadge = this.querySelector('[data-sale-badge]');
    this.sticky = this.querySelector('[data-sticky-cta]');
    this.stickyAdd = this.querySelector('[data-sticky-add]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this.errorMessage = this.querySelector('[data-product-error]');
    this.mediaScroller = this.querySelector('.wagmade-product__media-grid');
    this.galleryDots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.selectedVariant = this.findVariantFromOptions() || this.product.variants?.[0];
    this.isSubmitting = false;

    this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange.bind(this)));
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.stickyAdd?.addEventListener('click', () => this.form?.requestSubmit(this.addButton));

    this.setupStickyCta();
    this.setupMobileGallery();
    this.setupQuantityControls();
    this.loadFallbackRecommendations();
    this.setupAccordions();
    this.updateState(false);
    this.setupFitFinder();
    this.setupBundleBuilder();
  }

  findVariantFromOptions() {
    if (!this.product.variants?.length) return null;
    const selectedOptions = [];
    this.optionInputs.forEach((input) => {
      if (input.checked) {
        selectedOptions[Number(input.dataset.optionIndex)] = input.value;
      }
    });

    if (!selectedOptions.length) return this.product.variants.find((variant) => this.isVariantPurchasable(variant)) || this.product.variants[0];

    return this.product.variants.find((variant) => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  onOptionChange() {
    this.selectedVariant = this.findVariantFromOptions();
    this.updateState(true);
  }

  updateState(updateUrl) {
    const variant = this.selectedVariant;
    const available = this.isVariantPurchasable(variant);

    if (this.variantInput && variant) this.variantInput.value = variant.id;
    if (this.currentPrice && variant) this.currentPrice.textContent = this.formatMoney(variant.price);
    if (this.stickyPrice && variant) this.stickyPrice.textContent = this.formatMoney(variant.price);

    const installmentAmount = this.querySelector('[data-installment-amount]');
    if (installmentAmount && variant) {
      installmentAmount.textContent = this.formatMoney(Math.round(variant.price / 4));
    }

    if (this.comparePrice && this.saleBadge && variant?.compare_at_price > variant.price) {
      this.comparePrice.hidden = false;
      this.saleBadge.hidden = false;
      this.comparePrice.textContent = this.formatMoney(variant.compare_at_price);
    } else if (this.comparePrice && this.saleBadge) {
      this.comparePrice.hidden = true;
      this.saleBadge.hidden = true;
      this.comparePrice.textContent = '';
    }

    this.setButtonState(available ? 'Add to Cart' : 'Sold Out', !available);
    this.updateOptionAvailability();
    this.updateMedia();

    if (updateUrl && variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  setButtonState(text, disabled) {
    if (this.addButtonText) this.addButtonText.textContent = text;
    if (this.addButton) this.addButton.disabled = disabled;
    if (this.stickyAdd) {
      this.stickyAdd.textContent = text;
      this.stickyAdd.disabled = disabled;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form || this.isSubmitting || !this.isVariantPurchasable(this.selectedVariant)) return;

    this.isSubmitting = true;
    this.showError('');
    this.setButtonState('Adding...', true);

    let addedItem = null;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const formData = new FormData(this.form);
      formData.delete('return_to');
      formData.append('sections', 'wagmade-cart-drawer');
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Add to cart failed');

      addedItem = await response.json().catch(() => null);
    } catch (error) {
      this.handleAddFailure();
      return;
    }

    try {
      await this.showCartAfterAdd(addedItem);
    } catch (error) {
      this.goToCartPage();
      return;
    }

    this.isSubmitting = false;
    this.setButtonState('Added', false);
    setTimeout(() => this.updateState(false), 1400);
  }

  async showCartAfterAdd(addResponse) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer?.open) throw new Error('Cart drawer missing');

    const drawerSection = addResponse?.sections?.['wagmade-cart-drawer'];
    if (drawerSection && drawer.renderSection) {
      try {
        drawer.renderSection(drawerSection);
      } catch (error) {
        await this.refreshDrawer(drawer, addResponse);
      }
    } else {
      await this.refreshDrawer(drawer, addResponse);
    }

    drawer.open();
  }

  async refreshDrawer(drawer, addResponse) {
    if (drawer.refreshFromSection) {
      await drawer.refreshFromSection('/cart');
      return;
    }

    throw new Error('Cart drawer section unavailable');
  }

  getAddedLineItem(addResponse) {
    if (!addResponse) return null;
    if (addResponse.id) return addResponse;
    if (Array.isArray(addResponse.items) && addResponse.items[0]?.id) return addResponse.items[0];
    return null;
  }

  handleAddFailure() {
    this.isSubmitting = false;
    this.updateState(false);
    this.showError('We could not add this item to your cart. Please try again.');
  }

  goToCartPage() {
    const root = window.Shopify?.routes?.root || '/';
    window.location.assign(`${root}cart`);
  }

  showError(message) {
    if (!this.errorMessage) return;

    this.errorMessage.textContent = message;
    this.errorMessage.hidden = message === '';
  }

  setupStickyCta() {
    if (!this.sticky || !this.addButton) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.sticky.toggleAttribute('data-visible', !entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    observer.observe(this.addButton);
  }

  setupQuantityControls() {
    const wrappers = this.querySelectorAll('[data-quantity-wrapper]');
    wrappers.forEach((wrapper) => {
      const input = wrapper.querySelector('[data-quantity-input]');
      const minusBtn = wrapper.querySelector('[data-quantity-minus]');
      const plusBtn = wrapper.querySelector('[data-quantity-plus]');
      if (!input) return;

      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          const val = parseInt(input.value) || 1;
          if (val > 1) {
            input.value = val - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          const val = parseInt(input.value) || 1;
          input.value = val + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  setupMobileGallery() {
    if (!this.mediaScroller || !this.galleryDots.length) return;

    this.mediaScroller.addEventListener(
      'scroll',
      () => {
        const width = this.mediaScroller.clientWidth || 1;
        const index = Math.round(this.mediaScroller.scrollLeft / width);
        this.galleryDots.forEach((dot, dotIndex) => {
          dot.toggleAttribute('data-active', dotIndex === index);
        });
      },
      { passive: true },
    );
  }

  updateOptionAvailability() {
    const selectedOptions = [];
    this.optionInputs.forEach((input) => {
      if (input.checked) selectedOptions[Number(input.dataset.optionIndex)] = input.value;
    });

    this.optionInputs.forEach((input) => {
      const candidateOptions = [...selectedOptions];
      candidateOptions[Number(input.dataset.optionIndex)] = input.value;
      const candidateVariant = this.product.variants.find((variant) => {
        return variant.options.every((option, index) => option === candidateOptions[index]);
      });

      const unavailable = !candidateVariant || !this.isVariantPurchasable(candidateVariant);
      input.toggleAttribute('data-unavailable', unavailable);
      input.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
    });
  }

  isVariantPurchasable(variant) {
    if (!variant) return false;
    return Boolean(variant.id && variant.available);
  }

  updateMedia() {
    const mediaId = this.selectedVariant?.featured_media?.id;
    if (!mediaId || !this.mediaScroller) return;

    const media = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!media) return;

    if (window.matchMedia('(max-width: 989px)').matches) {
      media.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }

  async loadFallbackRecommendations() {
    const target = this.querySelector('[data-recommendations-url]');
    if (!target || this.querySelector('.wagmade-product__complete')) return;

    try {
      const response = await fetch(target.dataset.recommendationsUrl);
      if (!response.ok) return;

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const fallback = documentFragment.querySelector('.wagmade-product__complete');
      if (fallback) {
        target.replaceWith(fallback);
        this.setupBundleBuilder();
      }
    } catch (error) {
      target.remove();
    }
  }

  setupAccordions() {
    const accordions = this.querySelectorAll('.wagmade-product__accordion');
    accordions.forEach((details) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.wagmade-product__accordion-content');
      if (!summary || !content) return;

      summary.addEventListener('click', (e) => {
        e.preventDefault();

        if (details.hasAttribute('open')) {
          details.classList.add('is-closing');
          const height = content.offsetHeight;
          content.style.height = `${height}px`;

          content.offsetHeight; // trigger reflow
          content.style.height = '0px';

          const onTransitionEnd = (evt) => {
            if (evt.propertyName === 'height') {
              details.removeAttribute('open');
              details.classList.remove('is-closing');
              content.style.height = '';
              content.removeEventListener('transitionend', onTransitionEnd);
            }
          };
          content.addEventListener('transitionend', onTransitionEnd);
        } else {
          details.setAttribute('open', '');
          details.classList.add('is-opening');

          const height = content.scrollHeight;
          content.style.height = '0px';

          content.offsetHeight; // trigger reflow
          content.style.height = `${height}px`;

          const onTransitionEnd = (evt) => {
            if (evt.propertyName === 'height') {
              details.classList.remove('is-opening');
              content.style.height = '';
              content.removeEventListener('transitionend', onTransitionEnd);
            }
          };
          content.addEventListener('transitionend', onTransitionEnd);
        }
      });
    });
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  /* ==========================================================================
     高转化优化交互方法
     ========================================================================== */
  setupFitFinder() {
    const triggers = this.querySelectorAll('[data-fit-finder-trigger]');
    const overlay = this.querySelector('[data-fit-finder-overlay]');
    const closeBtn = this.querySelector('[data-fit-finder-close]');

    if (!overlay) return;

    const openModal = () => {
      overlay.setAttribute('data-open', '');
      document.documentElement.setAttribute('data-cart-lock', '');
    };

    const closeModal = () => {
      overlay.removeAttribute('data-open');
      document.documentElement.removeAttribute('data-cart-lock');
    };

    triggers.forEach(trigger => trigger.addEventListener('click', openModal));
    closeBtn?.addEventListener('click', closeModal);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeModal();
    });

    // Esc 键关闭
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.hasAttribute('data-open')) {
        closeModal();
      }
    });
  }

  setupBundleBuilder() {
    const bundleSection = this.querySelector('[data-bundle-section]');
    if (!bundleSection) return;

    // 获取主产品价格（美分）
    const mainPriceCents = parseInt(bundleSection.dataset.mainPrice, 10);
    const discountEnabled = bundleSection.dataset.bundleDiscountEnabled === 'true';
    const checkboxes = bundleSection.querySelectorAll('[data-bundle-checkbox]');
    const bundleBar = bundleSection.querySelector('[data-bundle-bar]');
    const oldPriceEl = bundleSection.querySelector('[data-bundle-old-total]');
    const newPriceEl = bundleSection.querySelector('[data-bundle-new-total]');
    const submitBtn = bundleSection.querySelector('[data-bundle-submit-btn]');
    const errorMsg = bundleSection.querySelector('[data-bundle-error-msg]');

    const calculateBundlePrice = () => {
      let totalCents = mainPriceCents;
      let selectedCount = 0;

      checkboxes.forEach(box => {
        if (box.checked) {
          const itemRow = box.closest('[data-bundle-item]');
          totalCents += parseInt(itemRow.dataset.priceCents, 10);
          selectedCount++;
        }
      });

      if (selectedCount > 0) {
        if (discountEnabled) {
          const discountedCents = Math.round(totalCents * 0.90);
          oldPriceEl.textContent = this.formatMoney(totalCents);
          newPriceEl.textContent = this.formatMoney(discountedCents);
          oldPriceEl.hidden = false;
        } else {
          oldPriceEl.textContent = '';
          oldPriceEl.hidden = true;
          newPriceEl.textContent = this.formatMoney(totalCents);
        }
        
        bundleBar.hidden = false;
      } else {
        bundleBar.hidden = true;
      }
    };

    checkboxes.forEach(box => {
      box.addEventListener('change', () => {
        const itemRow = box.closest('[data-bundle-item]');
        itemRow.classList.toggle('active', box.checked);
        calculateBundlePrice();
      });
    });

    if (submitBtn) {
      submitBtn.addEventListener('click', async (e) => {
        e.preventDefault();

        const itemsToAdd = [];
        
        // 1. 添加当前主变体 ID
        if (this.selectedVariant) {
          itemsToAdd.push({
            id: parseInt(this.selectedVariant.id, 10),
            quantity: 1
          });
        }

        // 2. 添加所有已勾选推荐单品的变体 ID
        checkboxes.forEach(box => {
          if (box.checked) {
            const itemRow = box.closest('[data-bundle-item]');
            itemsToAdd.push({
              id: parseInt(itemRow.dataset.variantId, 10),
              quantity: 1
            });
          }
        });

        if (itemsToAdd.length <= 1) return; // 必须是勾选了搭配单品

        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Adding set...';
        submitBtn.disabled = true;
        if (errorMsg) errorMsg.hidden = true;

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: itemsToAdd,
              sections: ['wagmade-cart-drawer'],
              sections_url: '/cart',
            }),
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.description || data.message || 'Could not add set to cart');
          }

          submitBtn.textContent = 'Added Set!';
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 2000);

          // 批量加购完成后，调起 Cart Drawer 侧边购物车
          const drawer = document.querySelector('wagmade-cart-drawer') || document.querySelector('[data-cart-drawer]');
          if (drawer) {
            try {
              const html = data?.sections?.['wagmade-cart-drawer'];
              if (html && drawer.renderSection) {
                drawer.renderSection(html);
              } else if (drawer.refreshFromSection) {
                await drawer.refreshFromSection('/cart');
              } else {
                throw new Error('Cart drawer section unavailable');
              }
            } catch (_) {
              window.location.assign(`${root}cart`);
              return;
            }
            if (drawer.open) drawer.open();
          } else {
            window.location.assign(`${root}cart`);
            return;
          }
        } catch (err) {
          submitBtn.textContent = 'Error Adding';
          if (errorMsg) {
            errorMsg.textContent = err.message || 'An error occurred.';
            errorMsg.hidden = false;
          }
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 2000);
        }
      });
    }
  }
}

if (!customElements.get('wagmade-product')) {
  customElements.define('wagmade-product', WagmadeProduct);
}
